// TERRA hazards — the gameplay half of the biome features. Samples each unit's ground hazard on a
// throttled tick (4 Hz — hundreds of units cost nothing) and applies:
//  • LAVA: burn damage through the sides' own damage() (typed "explosion", so giants/tanks resist
//    per their existing explosion resists — tough units genuinely CAN ford a channel; grunts price
//    it via the weighted pathfinding instead),
//  • player effects every frame (cheap, one sample): lava burn + ice-slick drift (envAccelMul).

import type { TerraField } from "../terra/field";
import { HAZ_LAVA, HAZ_LAVA_DEEP, HAZ_SLICK, HAZ_WATER_SHALLOW } from "../terra/field";
import type { CombatSide } from "./projectiles";
import type { Player } from "../entities/player";

const TICK = 0.25; // s between NPC hazard ticks
const LAVA_DPS = 14; // pre-resist burn (little: dead in ~7s of wading — fords are short or fatal)

export class Hazards {
  private field: TerraField | null = null;
  private sides: CombatSide[] = [];
  private acc = 0;

  setField(field: TerraField | null): void {
    this.field = field;
  }

  setSides(sides: CombatSide[]): void {
    this.sides = sides;
  }

  update(dt: number): void {
    const f = this.field;
    if (!f) return;
    this.acc += dt;
    if (this.acc < TICK) return;
    this.acc -= TICK;
    for (const side of this.sides) {
      for (const u of side.units()) {
        if (u.dead || u.active === false) continue;
        const hz = f.hazardAt(u.pos.x, u.pos.z);
        if (hz === HAZ_LAVA || hz === HAZ_LAVA_DEEP) side.damage(u, LAVA_DPS * TICK, "explosion");
      }
    }
  }

  /** Per-frame player effects (one grid sample). Call only while playing. */
  playerTick(player: Player, dt: number): void {
    const f = this.field;
    if (!f) { player.envAccelMul = 1; return; }
    const hz = f.hazardAt(player.position.x, player.position.z);
    player.envAccelMul = hz === HAZ_SLICK ? 0.3 : hz === HAZ_WATER_SHALLOW ? 0.62 : 1;
    if (hz === HAZ_LAVA || hz === HAZ_LAVA_DEEP) player.damage(LAVA_DPS * dt);
  }
}
